const MONTH_MAP = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };

const parseTallyShortDate = (d) => {
  // "14-Sep-26" -> "2026-09-14"
  const parts = (d || '').split('-');
  if (parts.length !== 3) return null;
  const [day, mon, yy] = parts;
  const month = MONTH_MAP[mon];
  if (!month) return null;
  return `20${yy}-${month}-${day.padStart(2, '0')}`;
};

const detectFuelType = (text) => {
  if (!text) return null;
  if (/diesel/i.test(text)) return 'Diesel';
  if (/petrol|\bms\b/i.test(text)) return 'Petrol';
  if (/xtra|\bxg\b/i.test(text)) return 'Xtragreen';
  return null;
};

// Parses Tally's "flat" report XML export (one long stream of tags, not
// nested per-record) into { entries, error } ready for the credit-sales review table.
// A record's fields all fall between the start and its own <DSPEXPLVCHNUMBER> tag.
export async function parseTallyXmlText(xmlText, partyName) {
  // Match only the specific leaf tags we need - matching any <TAG> generically would let
  // the lazy regex swallow the whole file as one "ENVELOPE" match (its closing tag is
  // only at the very end, so there's nothing to stop it early).
  const tagRegex = /<(DSPVCHDATE|DSPVCHTYPE|DSPVCHDRAMT|DSPVCHLEDACCOUNT|DSPVCHEXPLACCOUNT|VCHLEDNARREXPLOSION|DSPEXPLVCHNUMBER)>([\s\S]*?)<\/\1>/g;
  const records = [];
  let current = { date: null, ledgerHint: null };
  let match;
  while ((match = tagRegex.exec(xmlText)) !== null) {
    const [, name, rawValue] = match;
    const value = rawValue.trim();
    if (name === 'DSPVCHDATE') {
      if (value && !current.date) current.date = value;
    } else if (name === 'DSPVCHTYPE') {
      current.vchType = value;
    } else if (name === 'DSPVCHDRAMT') {
      if (value) current.amount = Math.abs(parseFloat(value));
    } else if (name === 'DSPVCHLEDACCOUNT' || name === 'DSPVCHEXPLACCOUNT') {
      if (value && value !== '(as per details)' && !/round off/i.test(value)) {
        current.ledgerHint = value;
      }
    } else if (name === 'VCHLEDNARREXPLOSION') {
      current.narration = value;
    } else if (name === 'DSPEXPLVCHNUMBER') {
      current.voucherNo = value.replace(/^\(No\.\s*:\s*/, '').replace(/\)$/, '').trim();
      records.push(current);
      current = { date: null, ledgerHint: null };
    }
  }

  const saleRecords = records.filter(r => r.vchType === 'Sale' && r.date && r.amount !== undefined);
  if (saleRecords.length === 0) {
    return { entries: [], error: 'No Sale vouchers found in this XML.' };
  }

  // Fetch each unique date's fuel rate once (needed to back-calculate quantity from the amount)
  const uniqueDates = [...new Set(saleRecords.map(r => parseTallyShortDate(r.date)).filter(Boolean))];
  const rateByDate = {};
  for (const isoDate of uniqueDates) {
    try {
      const res = await fetch(`/api/rates?date=${isoDate}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      rateByDate[isoDate] = await res.json();
    } catch {
      rateByDate[isoDate] = null;
    }
  }

  const entries = saleRecords.map(r => {
    const isoDate = parseTallyShortDate(r.date);
    const narration = r.narration || '';
    const isCash = /cash/i.test(narration);
    const vbMatch = narration.match(/VEHICLE\s*NO\/BILL\s*No\.?\s*([^\s/]+)\/(\d+)/i);
    const vehicleNo = vbMatch ? vbMatch[1] : '';
    const billNo = vbMatch ? vbMatch[2] : '';

    let itemName = null, quantity = 0, rate = null, amount = 0, cashAmount = 0;
    if (isCash) {
      cashAmount = r.amount;
      itemName = 'CASH';
    } else {
      amount = r.amount;
      const fuelType = detectFuelType(r.ledgerHint);
      itemName = fuelType;
      const rates = rateByDate[isoDate];
      if (fuelType && rates) {
        rate = parseFloat(rates[fuelType.toLowerCase()]) || null;
        if (rate) quantity = Math.round((amount / rate) * 100) / 100;
      }
    }

    return {
      id: Date.now() + Math.random(),
      business_date: isoDate,
      party_name: partyName,
      item_name: itemName,
      quantity,
      amount,
      vehicle_no: vehicleNo,
      bill_no: billNo,
      voucher_no: r.voucherNo,
      rate,
      cash_amount: cashAmount
    };
  });

  return { entries, error: null };
}
